/**
 * Industry / keyword landing pages for serviceapp.ai SEO.
 * Each slug is a real route with its own title, meta, copy and structured data.
 */

import { EXTRA_INDUSTRY_SEO_PAGES, type IndustrySeoPage } from './industrySeoExtras.js';

export type { IndustrySeoPage } from './industrySeoExtras.js';

const BASE_KEYWORDS =
  'ServiceApp, ServiceApp.ai, service app, service business app, small service business app, service software, business app, small business app, small business operating system, small business management software, business automation software, AI for small business, field service app, field service management software, how to run a small business';

export const INDUSTRY_SEO_PAGES: IndustrySeoPage[] = [
  {
    slug: '/cleaning-service-software',
    keyword: 'cleaning service business software',
    title: 'Cleaning Service Software | Scheduling, Quotes & Invoicing — ServiceApp.ai',
    description:
      'ServiceApp.ai is cleaning service business software for residential and commercial teams. Capture leads, send WhatsApp quotes, schedule crews, invoice and get paid in one field service app. Grow trial, unlimited crew.',
    keywords: `cleaning service business software, cleaning business software, cleaning company app, maid service software, janitorial software, residential cleaning software, commercial cleaning software, ${BASE_KEYWORDS}`,
    h1: 'Cleaning service software',
    h1Accent: 'built for your crew.',
    subheadline:
      'Stop losing jobs to missed calls and WhatsApp chaos. ServiceApp.ai is the cleaning service business software that runs leads, recurring jobs, crew schedules, checklists, invoices and payments in one simple app.',
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Cleaning business owner reviewing revenue, schedules and invoices in ServiceApp',
    tradeLabel: 'Cleaning companies',
    features: [
      {
        title: 'Capture every cleaning enquiry',
        description:
          'Phone, WhatsApp and website leads land in one Enquiries tab — nothing slips to a competitor.',
      },
      {
        title: 'Recurring residential & commercial jobs',
        description:
          'Set up weekly, fortnightly or monthly contracts. The calendar auto-schedules your crews.',
      },
      {
        title: 'WhatsApp quotes customers open',
        description: 'Send a branded quote in under a minute. One tap confirms it into a booked job.',
      },
      {
        title: 'Same-day invoicing',
        description:
          'Invoice generates when the job is marked complete. Payment link goes out automatically.',
      },
      {
        title: 'Crew checklists on any phone',
        description:
          'Field teams see their route, job details and room checklists — no calls back to the office.',
      },
      {
        title: 'AI assistant Ava',
        description:
          'Answers enquiries after hours, drafts quotes and follows up on open jobs automatically.',
      },
    ],
    faqs: [
      {
        q: 'What is the best cleaning service business software?',
        a: 'ServiceApp.ai is field service software built for cleaning companies. It handles enquiries, WhatsApp quotes, recurring schedules, crew dispatch, checklists, invoicing and online payments — with unlimited crew and crew logins stay free.',
      },
      {
        q: 'Does it work for both residential and commercial cleaning?',
        a: 'Yes. Set up different service types, pricing tiers and recurring schedules for homes, offices, warehouses and facility contracts — all in one account.',
      },
      {
        q: 'Can my cleaners use it on their phone?',
        a: "Yes. Crews open ServiceApp on any phone or tablet — no app store download needed. They see today's jobs, routes, customer details and complete service reports on site.",
      },
      {
        q: 'How much does cleaning service software cost?',
        a: 'ServiceApp has a Start from $49/mo for owner-operators, plus paid plans from $149/month based on office users — crew logins stay free. Every plan includes unlimited crew.',
      },
    ],
    relatedSlugs: [
      '/cleaning-service-app',
      '/janitorial-software',
      '/maid-service-software',
      '/field-service-management-software',
    ],
  },
  {
    slug: '/janitorial-software',
    keyword: 'janitorial software',
    title: 'Janitorial Software | Commercial Cleaning Management — ServiceApp.ai',
    description:
      'Janitorial software for commercial cleaning contractors. Manage multi-site contracts, crew schedules, SLAs, invoicing and payments in one platform. Free trial from ServiceApp.ai.',
    keywords: `janitorial software, janitorial business software, commercial cleaning software, facility cleaning app, ${BASE_KEYWORDS}`,
    h1: 'Janitorial software',
    h1Accent: 'for commercial contracts.',
    subheadline:
      'Run multi-site janitorial contracts with crew dispatch, SLA tracking, consolidated billing and real-time job updates — without spreadsheets.',
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Janitorial business owner managing contracts and crews in ServiceApp',
    tradeLabel: 'Janitorial & commercial',
    features: [
      {
        title: 'Multi-site contract management',
        description: 'One dashboard for all client sites, schedules and SLAs.',
      },
      {
        title: 'Crew rotation scheduling',
        description: 'Assign teams across sites with drag-and-drop calendar.',
      },
      {
        title: 'Consolidated invoicing',
        description: 'Bill multiple sites under one client with branded invoices.',
      },
      {
        title: 'Service reports with photos',
        description: 'Crews upload before/after photos and completion checklists on site.',
      },
      {
        title: 'WhatsApp quote and confirm',
        description: 'Send quotes for new contracts and get instant confirmation.',
      },
      {
        title: 'Payments and reconciliation',
        description: 'Payment links, receipts and bank reconciliation built in.',
      },
    ],
    faqs: [
      {
        q: 'Is ServiceApp janitorial software or just residential?',
        a: 'Both. ServiceApp handles residential one-off cleans and large commercial janitorial contracts with recurring schedules, multi-site billing and team dispatch.',
      },
      {
        q: 'Can I manage multiple buildings under one client?',
        a: 'Yes. Create separate job sites under one customer account and bill them together or separately.',
      },
    ],
    relatedSlugs: ['/cleaning-service-software', '/facility-management-software'],
  },
  {
    slug: '/maid-service-software',
    keyword: 'maid scheduling software',
    title: 'Maid Scheduling Software | Booking & Dispatch — ServiceApp.ai',
    description:
      'Maid scheduling software for recurring cleans, online booking, WhatsApp quotes and crew dispatch. Stop double-booking and chase fewer payments. Free trial from ServiceApp.ai.',
    keywords: `maid scheduling software, maid scheduling, maid service software, house cleaning app, maid booking software, ${BASE_KEYWORDS}`,
    h1: 'Maid scheduling software',
    h1Accent: 'that fills your calendar.',
    subheadline:
      'Maid scheduling software that captures enquiries, sends quotes on WhatsApp, books jobs and dispatches your team — so you stop double-booking and chasing payments.',
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Maid service owner managing recurring bookings and payments in ServiceApp',
    tradeLabel: 'Maid services',
    features: [
      {
        title: 'Online booking page',
        description: 'Customers book directly from your branded booking page.',
      },
      {
        title: 'Maid scheduling calendar',
        description: 'See every job, every cleaner, every day on one screen.',
      },
      {
        title: 'Recurring home cleans',
        description: 'Weekly and fortnightly jobs auto-repeat on the calendar.',
      },
      {
        title: 'WhatsApp quotations',
        description: 'Quote new customers where they already are — on WhatsApp.',
      },
      { title: 'Customer history', description: 'Every past job, quote and payment in one customer record.' },
      {
        title: 'Get paid before you leave',
        description: 'Send a payment link with every invoice. Money same day.',
      },
    ],
    faqs: [
      {
        q: 'What is maid scheduling software?',
        a: 'Maid scheduling software helps house cleaning businesses manage bookings, assign cleaners, track recurring visits and invoice customers. ServiceApp.ai includes all of this plus WhatsApp quotes and an AI assistant.',
      },
      {
        q: 'Is maid scheduling different from maid service software?',
        a: 'Maid scheduling is the calendar and crew assignment layer. ServiceApp.ai covers scheduling plus quotes, dispatch, invoicing and payments so maid services run the whole job lifecycle in one app.',
      },
    ],
    relatedSlugs: [
      '/maid-scheduling-software',
      '/cleaning-service-software',
      '/field-service-management-software',
    ],
  },
  {
    slug: '/pest-control-software',
    keyword: 'pest control software',
    title: 'Pest Control Software | Treatment Records & Scheduling — ServiceApp.ai',
    description:
      'Pest control software for scheduling treatments, compliance reports, follow-up visits and invoicing. ServiceApp.ai — field service app built for pest control companies. Free trial.',
    keywords: `pest control software, pest control business software, pest management app, termite treatment software, ${BASE_KEYWORDS}`,
    h1: 'Pest control software',
    h1Accent: 'with compliance built in.',
    subheadline:
      'Schedule treatments, log chemicals used, set follow-up reminders and send branded service reports — all from one pest control business app your technicians use on their phone.',
    heroImage: '/serviceapp-owner-pest.jpg',
    heroImageAlt: 'Pest control business owner tracking treatments, follow-ups and revenue in ServiceApp',
    tradeLabel: 'Pest control',
    features: [
      {
        title: 'Treatment scheduling',
        description: 'Book initial treatments and automatic follow-up visits.',
      },
      {
        title: 'Compliance service reports',
        description: 'Technicians complete reports with photos and treatment details on site.',
      },
      {
        title: 'Customer treatment history',
        description: 'Full record of every visit, chemical and warranty period per property.',
      },
      {
        title: 'Route-friendly dispatch',
        description: 'Assign technicians by zone and optimise daily routes.',
      },
      {
        title: 'WhatsApp quotes',
        description: 'Quote inspections and treatment plans via WhatsApp in one tap.',
      },
      {
        title: 'Recurring AMC contracts',
        description: 'Annual maintenance contracts auto-schedule quarterly visits.',
      },
    ],
    faqs: [
      {
        q: 'Does ServiceApp work for pest control companies?',
        a: 'Yes. Pest control teams use ServiceApp for enquiry capture, treatment scheduling, on-site service reports with photos, follow-up reminders and invoicing — all configurable per organisation.',
      },
    ],
    relatedSlugs: ['/field-service-management-software', '/cleaning-service-software'],
  },
  {
    slug: '/hvac-service-software',
    keyword: 'HVAC service software',
    title: 'HVAC Service Software | AMC Scheduling & Breakdown Calls — ServiceApp.ai',
    description:
      'HVAC service software for AC maintenance, AMC contracts, breakdown call dispatch and invoicing. ServiceApp.ai field service app for HVAC teams. Free trial, unlimited technicians.',
    keywords: `HVAC service software, AC service software, air conditioning business app, HVAC field service software, AMC scheduling software, ${BASE_KEYWORDS}`,
    h1: 'HVAC service software',
    h1Accent: 'for AMC and breakdowns.',
    subheadline:
      'Manage AMC contracts, schedule preventative maintenance, dispatch technicians to breakdown calls and invoice on completion — the HVAC field service app your technicians actually use.',
    heroImage: '/serviceapp-owner-hvac.jpg',
    heroImageAlt: 'HVAC business owner reviewing jobs, technicians and monthly revenue in ServiceApp',
    tradeLabel: 'AC & HVAC',
    features: [
      {
        title: 'AMC contract scheduling',
        description: 'Auto-schedule quarterly and annual AC servicing from contract start date.',
      },
      {
        title: 'Urgent breakdown dispatch',
        description: 'Log emergency calls and assign the nearest available technician instantly.',
      },
      {
        title: 'Technician mobile app',
        description: 'Techs see job details, customer history and service checklist on their phone.',
      },
      {
        title: 'Parts and job costing',
        description: 'Log parts used per job for accurate invoicing and margin tracking.',
      },
      {
        title: 'Service reports',
        description: 'Branded reports with before/after readings sent to the customer automatically.',
      },
      {
        title: 'Payment on completion',
        description: 'Invoice and payment link sent the moment the job is marked done.',
      },
    ],
    faqs: [
      {
        q: 'What is the best HVAC service software for small teams?',
        a: 'ServiceApp.ai is built for owner-operators and growing HVAC teams. Unlimited technicians on every plan, AMC scheduling, breakdown dispatch and WhatsApp quotes — from $149/month or Start from $49/mo.',
      },
    ],
    relatedSlugs: ['/field-service-management-software', '/plumbing-business-software'],
  },
  {
    slug: '/plumbing-business-software',
    keyword: 'plumbers job management app',
    title: 'Plumbers Job Management App | Schedule & Invoice — ServiceApp.ai',
    description:
      'Plumbers job management app for emergency calls, scheduling, quotes, dispatch and instant invoicing. Run every job from enquiry to payment. Free trial from ServiceApp.ai.',
    keywords: `plumbers job management app, plumbing job management app, plumbing business software, plumber scheduling software, plumbing service app, plumbing invoicing software, ${BASE_KEYWORDS}`,
    h1: 'Plumbers job management app',
    h1Accent: 'quote, schedule, get paid.',
    subheadline:
      'A job management app for plumbers: quote on the spot, schedule emergency and planned jobs, dispatch your crew and collect payment before you leave the site.',
    heroImage: '/serviceapp-owner-trades.jpg',
    heroImageAlt: 'Plumbing business owner reviewing quotes, jobs and outstanding invoices in ServiceApp',
    tradeLabel: 'Plumbing',
    features: [
      {
        title: 'Emergency call logging',
        description: 'Capture urgent calls with location and issue details in seconds.',
      },
      {
        title: 'On-site quoting',
        description: "Build and send a quote from your phone while still at the customer's property.",
      },
      {
        title: 'Job scheduling',
        description: 'Book follow-up jobs and assign the right plumber to each call.',
      },
      {
        title: 'Parts and labour tracking',
        description: 'Log materials and labour hours for accurate job costing.',
      },
      {
        title: 'Instant invoicing',
        description: 'Generate and send a tax-compliant invoice when the job is complete.',
      },
      {
        title: 'Payment before you leave',
        description: 'Customer pays via payment link on their phone — money in your bank same day.',
      },
    ],
    faqs: [
      {
        q: 'What is a plumbers job management app?',
        a: 'It is software that tracks plumbing jobs from first call through scheduling, dispatch, completion and payment. ServiceApp.ai is a plumbers job management app with WhatsApp quotes, unlimited crew and office-user pricing.',
      },
      {
        q: 'Is there plumbing business software for small companies?',
        a: 'Yes. ServiceApp.ai has a Start from $49/mo for solo plumbers and scales to multi-crew operations. No per-user fees — you pay for office users; crew stays free.',
      },
    ],
    relatedSlugs: [
      '/plumbers-job-management-app',
      '/handyman-business-software',
      '/field-service-management-software',
    ],
  },
  {
    slug: '/handyman-business-software',
    keyword: 'handyman quoting software',
    title: 'Handyman Quoting Software & Job App | ServiceApp.ai',
    description:
      'Handyman quoting software for on-the-spot quotes, multi-trade jobs, scheduling and payment collection. Send WhatsApp quotes and get paid the same day. Free trial.',
    keywords: `handyman quoting software, handyman quote app, handyman business software, handyman scheduling app, handyman invoicing software, ${BASE_KEYWORDS}`,
    h1: 'Handyman quoting software',
    h1Accent: 'and job management in one app.',
    subheadline:
      'Build flexible multi-trade quotes in minutes, send them on WhatsApp, schedule the job and get paid before you drive away — handyman quoting software built for the field.',
    heroImage: '/serviceapp-owner-trades.jpg',
    heroImageAlt: 'Handyman business owner quoting and invoicing jobs in ServiceApp',
    tradeLabel: 'Handyman services',
    features: [
      {
        title: 'Flexible multi-trade quoting',
        description: 'Line-item quotes for any combination of tasks in one job.',
      },
      {
        title: 'Same-day scheduling',
        description: 'Book jobs on the fly and see your full day on one calendar.',
      },
      { title: 'On-site payment', description: 'Send a payment link from your phone when the work is done.' },
      { title: 'Customer job history', description: 'See every past job, quote and invoice per customer.' },
      {
        title: 'WhatsApp quote send',
        description: 'Customers confirm quotes on WhatsApp — becomes a booking instantly.',
      },
      {
        title: 'Photo service reports',
        description: 'Document completed work with photos attached to the job record.',
      },
    ],
    faqs: [
      {
        q: 'What is handyman quoting software?',
        a: 'Handyman quoting software lets you build line-item quotes for multi-trade jobs, send them to customers and turn approvals into scheduled work. ServiceApp.ai also schedules, invoices and collects payment in the same app.',
      },
      {
        q: 'Is there a handyman quote app for mobile?',
        a: 'Yes. ServiceApp.ai works as a handyman quote app on any phone — build and send quotes on site, then collect payment when the job finishes.',
      },
    ],
    relatedSlugs: [
      '/handyman-quoting-software',
      '/handyman-quote-app',
      '/plumbing-business-software',
      '/field-service-management-software',
    ],
  },
  {
    slug: '/landscaping-business-software',
    keyword: 'landscaping business software',
    title: 'Landscaping Business Software | Seasonal Scheduling — ServiceApp.ai',
    description:
      'Landscaping business software for seasonal schedules, recurring visits, crew route planning and invoicing. ServiceApp.ai field service app. Free trial.',
    keywords: `landscaping business software, lawn care software, landscaping scheduling app, garden maintenance software, ${BASE_KEYWORDS}`,
    h1: 'Landscaping business software',
    h1Accent: 'seasonal jobs, year-round.',
    subheadline:
      'Plan seasonal schedules, manage recurring lawn care visits, dispatch crews by route and invoice every job automatically.',
    heroImage: '/serviceapp-hero-banner.png',
    heroImageAlt: 'Landscaping business owner planning seasonal schedules and revenue in ServiceApp',
    tradeLabel: 'Landscaping',
    features: [
      {
        title: 'Seasonal schedule planning',
        description: 'Set up spring, summer and autumn service windows in advance.',
      },
      {
        title: 'Recurring lawn visits',
        description: 'Weekly and monthly mowing jobs repeat automatically on the calendar.',
      },
      {
        title: 'Route-friendly crew dispatch',
        description: 'Assign crews by neighbourhood to minimise drive time.',
      },
      {
        title: 'Weather-day rescheduling',
        description: "Move jobs quickly when weather cancels a day's work.",
      },
      { title: 'Before/after photos', description: 'Crews upload photos as proof of work completed.' },
      { title: 'Contract invoicing', description: 'Bill monthly contracts or per-visit — your choice.' },
    ],
    faqs: [
      {
        q: 'Is ServiceApp good for lawn care and landscaping?',
        a: 'Yes. Landscaping and lawn care companies use ServiceApp for recurring visit scheduling, crew dispatch, route planning and automated invoicing.',
      },
    ],
    relatedSlugs: ['/field-service-management-software', '/cleaning-service-software'],
  },
  {
    slug: '/facility-management-software',
    keyword: 'facility management software',
    title: 'Facility Management Software | Multi-Site Contracts — ServiceApp.ai',
    description:
      'Facility management software for multi-site contracts, team dispatch, SLA tracking and consolidated billing. ServiceApp.ai field service platform. Free trial.',
    keywords: `facility management software, facility maintenance app, building maintenance software, multi-site service software, ${BASE_KEYWORDS}`,
    h1: 'Facility management software',
    h1Accent: 'multi-site, one platform.',
    subheadline:
      'Run contracts across multiple buildings with team dispatch, SLA tracking, consolidated billing and real-time job visibility — without enterprise complexity.',
    heroImage: '/serviceapp-hero-dashboard.png',
    heroImageAlt: 'Facility management business owner tracking multi-site contracts in ServiceApp',
    tradeLabel: 'Facility management',
    features: [
      {
        title: 'Multi-site contract management',
        description: 'One account, many buildings, separate or consolidated billing.',
      },
      { title: 'SLA tracking', description: 'Set response time targets and track compliance per contract.' },
      {
        title: 'Team dispatch across sites',
        description: 'Assign the right crew to the right building from one calendar.',
      },
      {
        title: 'Consolidated client reporting',
        description: 'Export job history and costs per client or per site.',
      },
      {
        title: 'Subcontractor management',
        description: 'Track jobs assigned to external teams alongside your own crews.',
      },
      {
        title: 'Automated invoicing',
        description: 'Monthly contract invoices generated and sent automatically.',
      },
    ],
    faqs: [
      {
        q: 'Is ServiceApp facility management software or just for small teams?',
        a: 'ServiceApp scales from solo operators to multi-site facility contractors. The Scale and Enterprise plans support multiple locations, custom domains and advanced reporting.',
      },
    ],
    relatedSlugs: ['/janitorial-software', '/field-service-management-software'],
  },
  {
    slug: '/field-service-management-software',
    keyword: 'field service management app',
    title: 'Field Service Management App & Software | ServiceApp.ai',
    description:
      'Field service management app (FSM) for scheduling, dispatch, quotes, invoicing and payments. Built for cleaning, HVAC, pest control and trade teams — not enterprise budgets. Free trial.',
    keywords: `field service management app, field services management app, field service management software, FSM software, field service software, mobile field service, work order management, ${BASE_KEYWORDS}`,
    h1: 'Field service management app',
    h1Accent: 'without enterprise complexity.',
    subheadline:
      'ServiceApp.ai is the field service management app (FSM) for small and growing service businesses — scheduling, dispatch, quotes, invoicing, payments and AI in one place, without Fortune 500 pricing.',
    heroImage: '/serviceapp-hero-dashboard.png',
    heroImageAlt: 'ServiceApp business management dashboard showing jobs, teams and revenue',
    tradeLabel: 'All field service teams',
    features: [
      {
        title: 'Job scheduling & dispatch',
        description: 'Drag-and-drop calendar with smart crew assignment.',
      },
      {
        title: 'Mobile field app',
        description: 'Technicians see jobs, routes and complete reports on any phone.',
      },
      {
        title: 'WhatsApp & email quotes',
        description: 'Send professional quotes where customers already are.',
      },
      {
        title: 'Work order management',
        description: 'Every job tracked from enquiry to completion to payment.',
      },
      { title: 'Automatic invoicing', description: 'Tax-compliant invoices generated from completed jobs.' },
      {
        title: 'AI assistant built in',
        description: 'Ava answers enquiries, drafts quotes and chases follow-ups 24/7.',
      },
    ],
    faqs: [
      {
        q: 'What is a field service management app?',
        a: 'A field service management app (also called FSM software) helps businesses that send technicians to customer locations — scheduling jobs, dispatching crews, tracking work completion, invoicing and collecting payment. ServiceApp.ai is an FSM app built for SMB service companies.',
      },
      {
        q: 'Is field service management app the same as field services management app?',
        a: 'Yes — people search both phrases. ServiceApp.ai covers field service / field services management: jobs, crews, quotes and payments in one app.',
      },
      {
        q: 'How is ServiceApp different from ServiceTitan or Jobber?',
        a: 'ServiceApp includes unlimited crew on every plan (crew logins stay free), WhatsApp-native quoting, a built-in AI assistant and a Start from $49/mo. It is designed for cleaning, HVAC, pest control and trade teams in any country.',
      },
      {
        q: 'Does ServiceApp work on mobile?',
        a: 'Yes. Field technicians use ServiceApp in their mobile browser — no app download required. They see jobs, upload photos and complete service reports on site.',
      },
    ],
    relatedSlugs: [
      '/field-service-app',
      '/field-service-software',
      '/cleaning-service-software',
      '/hvac-service-software',
      '/plumbers-job-management-app',
      '/handyman-quoting-software',
    ],
  },
  {
    slug: '/field-service-software',
    keyword: 'field service software',
    title: 'Field Service Software for Small Business | Simple & Affordable — ServiceApp.ai',
    description:
      'Field service software built for small and growing teams — not enterprise budgets. Schedule jobs, dispatch crews, send WhatsApp quotes, invoice and get paid in one app. Start from $49/mo, unlimited crew, crew logins stay free.',
    keywords: `field service software, field service software for small business, affordable field service software, simple field service software, field service app, service business software, ${BASE_KEYWORDS}`,
    h1: 'Field service software',
    h1Accent: 'small teams actually use.',
    subheadline:
      'Enterprise field service software is slow, expensive and built for 500-technician fleets. ServiceApp.ai gives growing service teams the same power — scheduling, dispatch, quotes, invoicing and AI — without the price tag or the setup pain.',
    heroImage: '/serviceapp-hero-banner.png',
    heroImageAlt: 'Small business owner running scheduling, quotes and invoicing in ServiceApp',
    tradeLabel: 'Small & growing service teams',
    features: [
      {
        title: 'Live in a day, not a quarter',
        description:
          'No consultants, no onboarding fees. Sign up, add your services and start booking jobs the same day.',
      },
      {
        title: 'Unlimited crew — no per-seat fees',
        description:
          'Add every technician, office user and crew member on any plan. You pay for job volume, never headcount.',
      },
      {
        title: 'Scheduling & dispatch',
        description:
          'Drag-and-drop calendar assigns the right person to the right job, with routes on their phone.',
      },
      {
        title: 'WhatsApp quotes & invoicing',
        description:
          'Quote and invoice where customers already are. One tap turns a quote into a booked job.',
      },
      {
        title: 'Payments built in',
        description:
          'Send a payment link with every invoice and reconcile automatically — money in same day.',
      },
      {
        title: 'AI assistant Ava included',
        description:
          'Answers enquiries after hours, drafts quotes and follows up on open jobs — no extra add-on.',
      },
    ],
    faqs: [
      {
        q: 'What is the best field service software for a small business?',
        a: 'ServiceApp.ai is field service software designed specifically for small and growing teams. Unlike IFS, Salesforce Field Service or Zoho FSM — which are built for large enterprises — ServiceApp is affordable, sets up in a day, includes unlimited crew and has a Start from $49/mo.',
      },
      {
        q: 'How much does field service software cost?',
        a: 'ServiceApp.ai has a Start from $49/mo for owner-operators and paid plans from $149/month based on office users — crew logins stay free. Enterprise field service platforms often cost hundreds of dollars per technician per month with long contracts and setup fees.',
      },
      {
        q: 'Do I need field service software if I already use WhatsApp and spreadsheets?',
        a: 'WhatsApp and spreadsheets break down as soon as you have more than a few jobs a week — missed messages, double bookings and unpaid invoices. Field service software keeps every enquiry, quote, job and payment in one place so nothing slips through.',
      },
      {
        q: 'Does ServiceApp work for any trade?',
        a: 'Yes. ServiceApp configures itself for cleaning, HVAC, pest control, plumbing, handyman, landscaping and facility management teams — any business that quotes, schedules and invoices field jobs.',
      },
    ],
    relatedSlugs: [
      '/field-service-management-software',
      '/field-service-app',
      '/cleaning-service-software',
      '/hvac-service-software',
    ],
  },
  {
    slug: '/small-business-software',
    keyword: 'small business software',
    title: 'Small Business Software | All-in-One App to Run Your Business — ServiceApp.ai',
    description:
      'Small business software that runs sales, scheduling, your team, invoicing and payments in one system. ServiceApp.ai is all-in-one small business management software with AI, unlimited crew and a Start from $49/mo — crew logins stay free.',
    keywords: `small business software, best small business software, all-in-one small business software, small business management software, software for small business, software to run my small business, best software for small business owners, business management app, streamline business operations, ${BASE_KEYWORDS}`,
    h1: 'Small business software',
    h1Accent: 'that runs the whole business.',
    subheadline:
      'Most small businesses end up with a tool for quotes, another for scheduling, a spreadsheet for money and WhatsApp for everything else. ServiceApp.ai is the small business software that replaces all of it — leads, jobs, team, invoices and numbers in one place.',
    heroImage: '/serviceapp-hero-dashboard.png',
    heroImageAlt:
      'Small business owner reviewing revenue, jobs and team performance on the ServiceApp dashboard',
    tradeLabel: 'Small business owners',
    features: [
      {
        title: 'One system of record',
        description:
          'Leads, customers, quotes, schedule, staff, invoices and payments share the same data — so nothing needs re-typing between apps.',
      },
      {
        title: 'The busywork runs itself',
        description:
          'Won quotes become scheduled jobs, completed jobs become invoices, and overdue invoices chase themselves.',
      },
      {
        title: 'Know your numbers daily',
        description:
          'Revenue, outstanding money, job profitability and team productivity update live — no month-end spreadsheet.',
      },
      {
        title: 'AI that does actual work',
        description:
          'Ava replies to enquiries around the clock, drafts quotes and follows up on the ones that went quiet.',
      },
      {
        title: 'Your whole team included',
        description:
          'Unlimited crew on every plan. You pay for job volume, so software cost tracks revenue instead of headcount.',
      },
      {
        title: 'Set up for your industry',
        description:
          'A setup wizard configures services, pricing, taxes and workflow for your trade — live the same day.',
      },
    ],
    faqs: [
      {
        q: 'What is the best small business software?',
        a: 'The best small business software covers your full workflow — not just invoicing or just CRM. ServiceApp.ai runs enquiries, quotes, scheduling, team management, invoicing, payments and reporting in one platform, with a Start from $49/mo, unlimited crew and a built-in AI assistant.',
      },
      {
        q: 'What is the best all-in-one software for a small business?',
        a: 'Look for one system of record where a won quote becomes a booked job and a completed job becomes an invoice. ServiceApp.ai is built that way so growing businesses do not need five subscriptions bolted together.',
      },
      {
        q: 'How can I streamline and automate my small business?',
        a: 'Start with the handoffs that leak time and money: quotes that never get followed up, jobs that never get invoiced, and invoices nobody chases. In ServiceApp those steps are automatic — an approved quote becomes a booked job, a completed job raises its invoice, and reminders go out on their own.',
      },
      {
        q: 'How do I use AI in my small business?',
        a: 'The practical wins are response speed and follow-up. Ava answers new enquiries instantly using your real services, pricing and availability, drafts quotes for approval, and reminds you about deals going cold.',
      },
      {
        q: 'Does ServiceApp replace my accounting software?',
        a: 'It handles the front half of your finances — quoting, invoicing, payment collection, expenses and reconciliation — with tax rules set for your country during setup. Most owners keep their accountant for filing and use ServiceApp as the system that produces clean numbers.',
      },
      {
        q: 'How much does small business software cost with ServiceApp?',
        a: 'There is a Start from $49/mo for owner-operators, with paid plans from $149/month based on office users rather than users. Every plan includes unlimited crew, and new workspaces get a 2-month full-access trial with no credit card.',
      },
    ],
    relatedSlugs: [
      '/how-to-run-a-small-business',
      '/business-app',
      '/service-app',
      '/service-business-app',
      '/field-service-software',
      '/cleaning-service-software',
    ],
  },
  {
    slug: '/service-app',
    keyword: 'service app',
    title: 'Service App for Small Business | Schedule, Quote & Get Paid — ServiceApp.ai',
    description:
      'ServiceApp.ai is the service app for small service businesses. Capture leads, send WhatsApp quotes, schedule jobs, dispatch your team, invoice and get paid in one app. Start from $49/mo, unlimited crew, crew logins stay free.',
    keywords: `service app, best service app, service app for small business, service business app, small service business app, app for service companies, ${BASE_KEYWORDS}`,
    h1: 'The service app',
    h1Accent: 'built for owners who do the work.',
    subheadline:
      'Stop running your service business from WhatsApp and spreadsheets. ServiceApp.ai is the service app that connects enquiries, quotes, schedules, crew, invoices and payments — so nothing slips while you are on a job.',
    heroImage: '/serviceapp-hero-banner.png',
    heroImageAlt: 'Service business owner using the ServiceApp service app on phone and laptop',
    tradeLabel: 'Service business owners',
    features: [
      {
        title: 'Every lead in one place',
        description:
          'Phone, WhatsApp and website enquiries land in one CRM — nothing lost to a missed message.',
      },
      {
        title: 'Quotes customers actually open',
        description: 'Send branded quotes on WhatsApp. One tap confirms and books the job.',
      },
      {
        title: 'Scheduling that fits a real crew',
        description:
          'Drag-and-drop calendar, recurring jobs and smart assignment without enterprise complexity.',
      },
      {
        title: 'Mobile for the field',
        description:
          'Your team sees jobs, routes and checklists on any phone — no app-store install required.',
      },
      {
        title: 'Invoices that write themselves',
        description: 'Mark the job complete and the invoice goes out with a payment link automatically.',
      },
      {
        title: 'AI that covers after hours',
        description: 'Ava answers enquiries, drafts quotes and follows up while you are on site or offline.',
      },
    ],
    faqs: [
      {
        q: 'What is a service app?',
        a: 'A service app is software that helps service businesses capture leads, quote work, schedule jobs, dispatch teams, invoice customers and collect payment. ServiceApp.ai is a service app built specifically for small and growing service companies — not enterprise fleets.',
      },
      {
        q: 'What is the best service app for a small business?',
        a: 'Look for a service app that covers the full workflow (not just scheduling), includes unlimited crew, and crew logins stay free. ServiceApp.ai includes WhatsApp quotes, dispatch, invoicing, payments and an AI assistant, with a Start from $49/mo.',
      },
      {
        q: 'Is ServiceApp a mobile service app?',
        a: 'Yes. Owners and field crews use ServiceApp in the browser on any phone or tablet. Quote from the driveway, complete reports on site and send payment links before you leave.',
      },
    ],
    relatedSlugs: [
      '/service-business-app',
      '/service-software',
      '/field-service-app',
      '/cleaning-service-app',
      '/small-business-software',
    ],
  },
  {
    slug: '/service-business-app',
    keyword: 'service business app',
    title: 'Service Business App | Quote, Schedule & Invoice — ServiceApp.ai',
    description:
      'The service business app for small service companies. Run leads, WhatsApp quotes, job scheduling, crew dispatch, invoicing and payments in one place. Unlimited crew · Start from $49/mo · free trial.',
    keywords: `service business app, best service business app, small service business app, app for service business, service company app, best app for service business, ${BASE_KEYWORDS}`,
    h1: 'Service business app',
    h1Accent: 'for growing teams.',
    subheadline:
      'The service business app that replaces your whiteboard, WhatsApp chaos and three other tools. Built for cleaning, trades, pest control, HVAC and any small team that quotes, schedules and invoices.',
    heroImage: '/serviceapp-hero-dashboard.png',
    heroImageAlt: 'Small service business dashboard showing jobs, team and revenue in ServiceApp',
    tradeLabel: 'Small service companies',
    features: [
      {
        title: 'Built for service workflows',
        description: 'Enquiry → quote → job → report → invoice → payment, without re-typing between apps.',
      },
      {
        title: 'Unlimited crew',
        description: 'Add every technician and office user on any plan. Pay for job volume, not headcount.',
      },
      {
        title: 'WhatsApp-native quoting',
        description: 'Customers open and confirm quotes where they already message you.',
      },
      {
        title: 'Owner dashboard',
        description: 'See revenue, outstanding invoices, booked jobs and team load in one view.',
      },
      {
        title: 'Industry setup wizard',
        description: 'Configure services, pricing and taxes for your trade in minutes.',
      },
      {
        title: 'AI office manager',
        description: 'Ava replies to leads and chases quiet quotes so growth does not mean more admin staff.',
      },
    ],
    faqs: [
      {
        q: 'What is the best service business app for a small company?',
        a: 'The best service business app covers the full job lifecycle — leads, quotes, scheduling, dispatch, invoicing and payments — with unlimited crew. ServiceApp.ai is built for that workflow with Start from $49/mo and a 2-month free trial.',
      },
      {
        q: 'What app do small service businesses need?',
        a: 'Most small service businesses need one app for CRM, quoting, scheduling, dispatch, invoicing and payments. ServiceApp.ai is that service business app — with a Start from $49/mo and unlimited crew.',
      },
    ],
    relatedSlugs: [
      '/service-app',
      '/service-software',
      '/field-service-app',
      '/field-service-management-software',
      '/how-to-run-a-small-business',
    ],
  },
  {
    slug: '/service-software',
    keyword: 'service software',
    title: 'Service Software for Small Business | Quotes to Payment — ServiceApp.ai',
    description:
      'Service software that runs your whole service company: leads, quotes, scheduling, dispatch, invoicing and payments. ServiceApp.ai — service software for small business with AI and unlimited crew. Free trial.',
    keywords: `service software, service business software, service company software, best service software, service management software, ${BASE_KEYWORDS}`,
    h1: 'Service software',
    h1Accent: 'from first enquiry to paid invoice.',
    subheadline:
      'Service software should not mean six logins. ServiceApp.ai connects sales, operations and money so a small service company can run professionally without enterprise cost or complexity.',
    heroImage: '/serviceapp-hero-dashboard.png',
    heroImageAlt: 'Service software dashboard with scheduling, invoices and team performance',
    tradeLabel: 'Service companies',
    features: [
      {
        title: 'End-to-end service management',
        description:
          'CRM, quotes, calendar, work orders, invoices and payments in one service software stack.',
      },
      {
        title: 'Field-ready',
        description: 'Technicians complete jobs and upload photos on mobile while the office stays in sync.',
      },
      {
        title: 'Automated handoffs',
        description:
          'Won quotes become jobs; completed jobs become invoices; overdue balances get reminders.',
      },
      {
        title: 'Tax-ready invoicing',
        description: 'GST, VAT or sales tax configured for your country during setup.',
      },
      {
        title: 'Office-user pricing',
        description: 'Never per seat. Software cost scales with the jobs you win.',
      },
      {
        title: 'AI included',
        description: 'Ava handles after-hours enquiries and follow-ups without an extra add-on fee.',
      },
    ],
    faqs: [
      {
        q: 'What is service software?',
        a: 'Service software (or service business software) helps companies that sell and deliver services — not products on a shelf — manage customers, jobs, teams and billing. ServiceApp.ai is service software designed for SMBs rather than Fortune 500 field fleets.',
      },
      {
        q: 'What is the best service software for a small company?',
        a: 'Choose service software that is fast to set up, includes unlimited crew and covers quoting through payment. ServiceApp.ai offers a Start from $49/mo, WhatsApp quotes and a built-in AI assistant.',
      },
    ],
    relatedSlugs: [
      '/service-app',
      '/service-business-app',
      '/field-service-software',
      '/small-business-software',
    ],
  },
  {
    slug: '/field-service-app',
    keyword: 'field service app',
    title: 'Field Service App for Small Business | Dispatch & Jobs — ServiceApp.ai',
    description:
      'Field service app for small teams. Schedule jobs, dispatch crews, complete mobile reports, send WhatsApp quotes and get paid faster. Also a field service management app without enterprise bloat. Free trial.',
    keywords: `field service app, best field service app, mobile field service app, field service app for small business, field service management app, field services management app, ${BASE_KEYWORDS}`,
    h1: 'Field service app',
    h1Accent: 'your crew will actually open.',
    subheadline:
      'A field service app should work on any phone, update the office in real time and not cost per technician. ServiceApp.ai gives small field teams scheduling, dispatch, job reports, quotes and payments without enterprise bloat.',
    heroImage: '/serviceapp-hero-banner.png',
    heroImageAlt: 'Technician using a field service app on a phone at a customer site',
    tradeLabel: 'Field service teams',
    features: [
      {
        title: 'Jobs on every phone',
        description:
          "Crews see today's route, customer notes and checklists without installing from an app store.",
      },
      {
        title: 'Smart dispatch',
        description: 'Assign the right person to the right job from a drag-and-drop calendar.',
      },
      {
        title: 'On-site proof of work',
        description: 'Photos and service reports attach to the job before you leave the site.',
      },
      {
        title: 'Quotes in the field',
        description: "Build and send a WhatsApp quote from the customer's driveway.",
      },
      {
        title: 'Same-day payment',
        description: 'Invoice + payment link when the job is marked complete.',
      },
      {
        title: 'AI after hours',
        description: 'Ava catches new enquiries while the crew is still finishing the last job.',
      },
    ],
    faqs: [
      {
        q: 'What is a field service app?',
        a: 'A field service app helps businesses that send technicians to customer locations schedule work, dispatch crews, track job completion and invoice. ServiceApp.ai is a field service app built for small and growing teams — not 500-tech fleets.',
      },
      {
        q: 'What is the best field service app for a small business?',
        a: 'ServiceApp.ai is designed for owner-operators and growing crews: unlimited crew, Start from $49/mo, WhatsApp quotes and mobile job reports — with crew logins free common in enterprise FSM tools.',
      },
      {
        q: 'Do I need to download an app from the store?',
        a: 'No. Field crews open ServiceApp in the mobile browser. They get jobs, maps, photos and reports without forcing customers or staff through app-store installs.',
      },
    ],
    relatedSlugs: [
      '/field-service-software',
      '/field-service-management-software',
      '/service-app',
      '/cleaning-service-app',
    ],
  },
  {
    slug: '/cleaning-service-app',
    keyword: 'cleaning service app',
    title: 'Cleaning Service App | Scheduling, Quotes & Crews — ServiceApp.ai',
    description:
      'Cleaning service app for residential and commercial teams. Capture leads, WhatsApp quotes, recurring schedules, crew checklists, invoicing and payments in one cleaning business app. Free trial from ServiceApp.ai.',
    keywords: `cleaning service app, cleaning business app, cleaning company app, maid service app, cleaning scheduling app, cleaning service software, ${BASE_KEYWORDS}`,
    h1: 'Cleaning service app',
    h1Accent: 'for crews that stay booked.',
    subheadline:
      'The cleaning service app that stops missed WhatsApp enquiries, double-booked cleaners and invoices that wait until Sunday night. Recurring jobs, checklists, quotes and payments — one system.',
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt:
      'Cleaning business owner managing schedules and invoices in the ServiceApp cleaning service app',
    tradeLabel: 'Cleaning companies',
    features: [
      {
        title: 'Recurring cleans on autopilot',
        description: 'Weekly, fortnightly and monthly contracts auto-schedule your crews.',
      },
      {
        title: 'Room and site checklists',
        description: 'Cleaners complete the right checklist on their phone at every visit.',
      },
      {
        title: 'WhatsApp quotes',
        description: 'Quote new homes and offices where customers already message you.',
      },
      {
        title: 'Crew calendar',
        description: 'See every job and cleaner on one screen — no double bookings.',
      },
      {
        title: 'Same-day invoicing',
        description: 'Invoice generates when the clean is marked done; payment link goes out automatically.',
      },
      {
        title: 'AI receptionist',
        description: 'Ava answers after-hours cleaning enquiries so you do not lose jobs overnight.',
      },
    ],
    faqs: [
      {
        q: 'What is the best cleaning service app?',
        a: 'The best cleaning service app covers leads, recurring schedules, crew dispatch, checklists, invoicing and payments. ServiceApp.ai is built for residential and commercial cleaning teams with unlimited crew and a Start from $49/mo.',
      },
      {
        q: 'Is a cleaning service app different from cleaning software?',
        a: 'People search both "cleaning service app" and "cleaning service software" for the same need. ServiceApp.ai works as both — a mobile-friendly cleaning business app with full back-office software underneath.',
      },
      {
        q: 'Can my cleaners use it without training?',
        a: "Yes. Cleaners open today's jobs on their phone, follow checklists and upload photos. Most teams are productive the same day they start.",
      },
    ],
    relatedSlugs: [
      '/cleaning-service-software',
      '/maid-service-software',
      '/janitorial-software',
      '/service-app',
      '/field-service-app',
    ],
  },
  {
    slug: '/business-app',
    keyword: 'business app',
    title: 'Business App for Small Business Owners | All-in-One — ServiceApp.ai',
    description:
      'Business app for small business owners who quote, schedule and invoice. Replace spreadsheets and WhatsApp with one small business app for sales, team, money and AI. Start from $49/mo from ServiceApp.ai.',
    keywords: `business app, small business app, best business app for small business, business management app, app to run my business, ${BASE_KEYWORDS}`,
    h1: 'Business app',
    h1Accent: 'that replaces five tools.',
    subheadline:
      'Most "business apps" do one thing. ServiceApp.ai is the small business app that runs leads, jobs, team, invoices and payments together — so you finally know what is booked, what is owed and what to do next.',
    heroImage: '/serviceapp-hero-dashboard.png',
    heroImageAlt: 'Small business owner using an all-in-one business app dashboard',
    tradeLabel: 'Small business owners',
    features: [
      {
        title: 'One login for the whole business',
        description: 'CRM, scheduling, team, invoicing and payments share the same customer and job data.',
      },
      {
        title: 'Built for operators, not IT teams',
        description: 'Live the same day — no consultants, no six-week implementation.',
      },
      {
        title: 'Money visibility daily',
        description: 'See revenue, unpaid invoices and job profitability without a month-end spreadsheet.',
      },
      {
        title: 'Team included free',
        description: 'Unlimited crew on every plan. Grow headcount without growing software cost.',
      },
      {
        title: 'Works on phone and desktop',
        description: 'Run the business between jobs from your phone; deep-dive on a laptop when you need to.',
      },
      {
        title: 'AI that does work',
        description: 'Ava replies, drafts quotes and flags what needs attention from your live data.',
      },
    ],
    faqs: [
      {
        q: 'What is the best business app for a small business?',
        a: 'The best business app depends on how you make money. If you quote work, schedule people and invoice customers, ServiceApp.ai is a strong all-in-one business app with a Start from $49/mo and unlimited crew.',
      },
      {
        q: 'Can one business app replace QuickBooks, a calendar and WhatsApp?',
        a: 'ServiceApp.ai replaces the operational stack — quotes, schedule, jobs, invoices and payment collection — and produces clean numbers for your accountant. Many owners keep filing software and use ServiceApp as the system that runs the business day to day.',
      },
    ],
    relatedSlugs: [
      '/small-business-software',
      '/how-to-run-a-small-business',
      '/service-app',
      '/service-business-app',
    ],
  },
  {
    slug: '/how-to-run-a-small-business',
    keyword: 'how to run a small business',
    title: 'How to Run a Small Business | Practical System for Owners — ServiceApp.ai',
    description:
      'How to run a small business without drowning in admin: capture every lead, quote fast, schedule work, invoice on completion and use AI for follow-ups. ServiceApp.ai is the system owners use to run a small service business.',
    keywords: `how to run a small business, how to run small business, how to run a service business, how to manage a small business, software to run a small business, run my small business, ${BASE_KEYWORDS}`,
    h1: 'How to run a small business',
    h1Accent: 'without living in your inbox.',
    subheadline:
      'Running a small business is not a motivation problem — it is a systems problem. Here is the practical operating rhythm owners use with ServiceApp.ai: one place for leads, quotes, jobs, team and money, with AI covering the gaps when you are busy delivering the work.',
    heroImage: '/serviceapp-hero-dashboard.png',
    heroImageAlt: 'Small business owner reviewing a simple system for running sales, jobs and money',
    tradeLabel: 'Owners & partners',
    features: [
      {
        title: '1. Capture every lead the same way',
        description:
          'Phone, WhatsApp and website enquiries go into one list with a next action — so hot leads never die in a chat thread.',
      },
      {
        title: '2. Quote while interest is high',
        description:
          'Send a branded WhatsApp quote in minutes. Fast quotes win more jobs than perfect quotes sent three days later.',
      },
      {
        title: '3. Schedule work you can actually deliver',
        description:
          'Book jobs against real capacity. Recurring work auto-repeats so retainers do not rely on memory.',
      },
      {
        title: '4. Invoice the day the job finishes',
        description:
          'Cash flow improves when invoicing is automatic. Payment links beat "I will send the bill later."',
      },
      {
        title: '5. Review numbers weekly, not yearly',
        description:
          'Check revenue, outstanding invoices and job load every week. Small course-corrections beat end-of-year surprises.',
      },
      {
        title: '6. Let AI cover after hours',
        description:
          'Ava answers new enquiries and nudges quiet quotes so growth does not require hiring a receptionist first.',
      },
    ],
    faqs: [
      {
        q: 'How do I run a small business day to day?',
        a: "Use one system of record for leads, quotes, schedule, delivery and money. Each morning, clear new enquiries and confirm today's jobs; each evening, make sure completed work is invoiced. ServiceApp.ai is built around that rhythm so the admin does not pile up overnight.",
      },
      {
        q: 'How do I run a small service business with a small team?',
        a: 'Standardise the handoffs: enquiry → quote → booked job → completed report → invoice → payment. Give every team member the same app for their piece, and keep unlimited crew so software cost does not punish hiring. That is the ServiceApp.ai model.',
      },
      {
        q: 'What software do I need to run a small business?',
        a: 'If you sell services, you need CRM, quoting, scheduling, invoicing and payments — ideally in one platform. ServiceApp.ai combines those with AI follow-ups and a Start from $49/mo so you are not paying for five subscriptions before you have steady revenue.',
      },
      {
        q: 'How can AI help me run my small business?',
        a: 'Use AI for speed and consistency: instant replies to new leads, draft quotes for your approval, reminders on cold quotes and a short list of what needs attention from your live numbers. Ava inside ServiceApp.ai does exactly that on your real services and pricing.',
      },
    ],
    relatedSlugs: [
      '/business-app',
      '/small-business-software',
      '/service-business-app',
      '/service-app',
      '/field-service-app',
      '/ai-business-intelligence',
    ],
  },
  {
    slug: '/ai-business-intelligence',
    keyword: 'AI business intelligence for service businesses',
    title: 'AI Business Intelligence — Analysis Reports & Next Actions — ServiceApp.ai',
    description:
      'AI Business Intelligence for service companies: analysis reports on conversion, bookings and cash flow, plus clear actions to improve the business. Personal configuration support available for setup.',
    keywords: `AI business intelligence, business analysis reports, service business improvement, AI business analysis, profitable service business, configuration support, ISO 9001 service company, ${BASE_KEYWORDS}`,
    h1: 'AI Business Intelligence',
    h1Accent: 'on your live operating system.',
    subheadline:
      'After ServiceApp runs your jobs end to end, AI analyses conversion, bookings and cash — with reports and recommended actions for owners and ops managers. Need setup help? Ask for configuration support.',
    heroImage: '/serviceapp-hero-dashboard.webp',
    heroImageAlt:
      'AI Business Intelligence dashboard showing insights, discrepancies and follow-ups for a service business',
    tradeLabel: 'Owners & growing operators',
    features: [
      {
        title: 'Insights from your real numbers',
        description:
          'Enquiries, conversion, bookings, revenue gaps and follow-ups — AI flags what to fix today, not a wall of charts.',
      },
      {
        title: 'Sell and upsell the right way',
        description:
          'See which services win, which quotes stall and which customers are ready for the next offer.',
      },
      {
        title: 'Cash flow and profit discipline',
        description:
          'Catch unpaid invoices, quiet accounts and cost drift before they become a month-end surprise.',
      },
      {
        title: 'Recommended next actions',
        description:
          'Each insight points to what to do next — follow-ups, repricing, capacity and cash — so improvement is practical.',
      },
      {
        title: 'Configuration support',
        description:
          'Personal help to configure ServiceApp for your business. Selected demo signups can request setup support; dedicated configuration help is available anytime.',
      },
      {
        title: 'ISO 9001-ready operations',
        description:
          'Documented jobs, checklists, reports and measurable processes — structure that supports an ISO 9001 application after disciplined implementation.',
      },
    ],
    faqs: [
      {
        q: 'What is AI Business Intelligence in ServiceApp?',
        a: 'AI-generated analysis on your live workspace — reports, discrepancies and recommended actions — so you can improve sell/upsell, cash flow and follow-ups. It complements Ava (the office AI) with owner-level decisions.',
      },
      {
        q: 'Do you offer configuration support?',
        a: 'Yes — personal help to configure ServiceApp for your business. AI Business Intelligence delivers analysis reports and recommended actions in-product; we do not sell ongoing personal business consulting.',
      },
      {
        q: 'Can implementing ServiceApp help with ISO 9001?',
        a: 'Yes. Using ServiceApp with discipline creates documented processes and evidence of work quality — a strong foundation to apply for ISO 9001. ServiceApp is not a certification body; it is the operating system that makes quality evidence part of everyday operations.',
      },
    ],
    relatedSlugs: [
      '/small-business-software',
      '/how-to-run-a-small-business',
      '/service-business-app',
      '/cleaning-service-software',
      '/facility-management-software',
    ],
  },
  {
    slug: '/handyman-quoting-software',
    keyword: 'handyman quoting software',
    title: 'Handyman Quoting Software | Instant Quotes & Jobs — ServiceApp.ai',
    description:
      'Handyman quoting software for line-item quotes, WhatsApp send, job booking and same-day payment. Built for multi-trade handymen. Free trial from ServiceApp.ai.',
    keywords: `handyman quoting software, handyman quote software, handyman estimating software, handyman quote app, ${BASE_KEYWORDS}`,
    h1: 'Handyman quoting software',
    h1Accent: 'that turns quotes into booked jobs.',
    subheadline:
      'Build line-item quotes for any mix of tasks, send them on WhatsApp, and convert approvals into scheduled jobs — handyman quoting software without a second spreadsheet.',
    heroImage: '/serviceapp-owner-trades.jpg',
    heroImageAlt: 'Handyman creating and sending a quote in ServiceApp',
    tradeLabel: 'Handyman services',
    features: [
      {
        title: 'Line-item quotes in minutes',
        description: 'Price multi-trade jobs with clear line items customers understand.',
      },
      {
        title: 'WhatsApp quote delivery',
        description: 'Send the quote where customers already reply — one tap to confirm.',
      },
      {
        title: 'Quote to job in one step',
        description: 'Approved quotes become scheduled work without re-typing.',
      },
      {
        title: 'On-site payment link',
        description: 'Invoice and collect when the job is done, before you leave.',
      },
      {
        title: 'Customer history',
        description: 'See past quotes, jobs and payments for every property.',
      },
      {
        title: 'Unlimited crew seats',
        description: 'Add helpers and office staff without per-user fees.',
      },
    ],
    faqs: [
      {
        q: 'What is the best handyman quoting software?',
        a: 'Look for quoting that connects to scheduling and payments. ServiceApp.ai is handyman quoting software that also books the job and invoices when work finishes.',
      },
    ],
    relatedSlugs: ['/handyman-quote-app', '/handyman-business-software', '/field-service-app'],
  },
  {
    slug: '/handyman-quote-app',
    keyword: 'handyman quote app',
    title: 'Handyman Quote App | Mobile Quotes & Payments — ServiceApp.ai',
    description:
      'Handyman quote app for phones: build quotes on site, send on WhatsApp, schedule the job and get paid. Free trial from ServiceApp.ai.',
    keywords: `handyman quote app, handyman quoting app, mobile handyman quote, handyman estimate app, ${BASE_KEYWORDS}`,
    h1: 'Handyman quote app',
    h1Accent: 'for quotes on the job site.',
    subheadline:
      'A handyman quote app that works on any phone — price the work while you are still at the property, send it instantly and book the follow-up without office rework.',
    heroImage: '/serviceapp-owner-trades.jpg',
    heroImageAlt: 'Handyman using a quote app on a phone at a customer site',
    tradeLabel: 'Handyman services',
    features: [
      {
        title: 'Mobile-first quoting',
        description: 'Create professional quotes from your phone between jobs.',
      },
      {
        title: 'WhatsApp send & confirm',
        description: 'Customers approve where they already message you.',
      },
      {
        title: 'Same-day scheduling',
        description: 'Turn a won quote into a calendar job in seconds.',
      },
      {
        title: 'Photo-backed jobs',
        description: 'Attach before/after photos to the job record.',
      },
      {
        title: 'Payment before you leave',
        description: 'Send a payment link with the invoice from the site.',
      },
      {
        title: 'Works in the browser',
        description: 'No app-store install required for you or your helpers.',
      },
    ],
    faqs: [
      {
        q: 'Is there a handyman quote app that also schedules jobs?',
        a: 'Yes. ServiceApp.ai is a handyman quote app that also schedules, dispatches and invoices — so quoting is not a dead-end document.',
      },
    ],
    relatedSlugs: ['/handyman-quoting-software', '/handyman-business-software', '/service-business-app'],
  },
  {
    slug: '/plumbers-job-management-app',
    keyword: 'plumbers job management app',
    title: 'Plumbers Job Management App | Schedule & Invoice — ServiceApp.ai',
    description:
      'Plumbers job management app for emergency calls, dispatch, quotes and invoicing. Track every job from first call to payment. Free trial from ServiceApp.ai.',
    keywords: `plumbers job management app, plumbing job management app, plumber job management software, plumber work order app, ${BASE_KEYWORDS}`,
    h1: 'Plumbers job management app',
    h1Accent: 'from first call to paid invoice.',
    subheadline:
      'Log emergency calls, assign plumbers, track parts and labour, and invoice when the job closes — a job management app built for plumbing teams.',
    heroImage: '/serviceapp-owner-trades.jpg',
    heroImageAlt: 'Plumber managing jobs, schedules and invoices in ServiceApp',
    tradeLabel: 'Plumbing',
    features: [
      {
        title: 'Emergency job intake',
        description: 'Capture caller, location and issue details in seconds.',
      },
      {
        title: 'Dispatch the right plumber',
        description: 'Assign by skill, zone or availability on one calendar.',
      },
      {
        title: 'On-site quotes',
        description: 'Price follow-up work while you are still at the property.',
      },
      {
        title: 'Parts & labour logging',
        description: 'Keep job costing accurate for every work order.',
      },
      {
        title: 'Instant invoicing',
        description: 'Generate the invoice when the job is marked complete.',
      },
      {
        title: 'Same-day payment links',
        description: 'Collect before you leave the site.',
      },
    ],
    faqs: [
      {
        q: 'What should a plumbers job management app include?',
        a: 'Intake, scheduling, dispatch, quoting, job notes, invoicing and payments. ServiceApp.ai covers that full loop for solo plumbers and multi-crew shops.',
      },
    ],
    relatedSlugs: [
      '/plumbing-business-software',
      '/field-service-management-software',
      '/handyman-quoting-software',
    ],
  },
  {
    slug: '/maid-scheduling-software',
    keyword: 'maid scheduling software',
    title: 'Maid Scheduling Software | Recurring Cleans & Crews — ServiceApp.ai',
    description:
      'Maid scheduling software for recurring cleans, cleaner assignment, online booking and invoicing. Stop double-booking. Free trial from ServiceApp.ai.',
    keywords: `maid scheduling software, maid scheduling, house cleaning schedule software, maid booking calendar, ${BASE_KEYWORDS}`,
    h1: 'Maid scheduling software',
    h1Accent: 'for recurring cleans and crews.',
    subheadline:
      'See every cleaner and every home on one calendar. Maid scheduling software that books recurring visits, avoids clashes and turns finished jobs into invoices.',
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Maid service calendar and crew schedule in ServiceApp',
    tradeLabel: 'Maid services',
    features: [
      {
        title: 'Recurring clean schedules',
        description: 'Weekly and fortnightly visits repeat automatically.',
      },
      {
        title: 'Crew assignment',
        description: 'Assign cleaners without spreadsheet conflicts.',
      },
      {
        title: 'Online booking',
        description: 'Customers book from your branded page into the same calendar.',
      },
      {
        title: 'WhatsApp quotes',
        description: 'Price new homes where customers already chat.',
      },
      {
        title: 'Route-friendly day view',
        description: 'See the full day before cleaners leave for the first job.',
      },
      {
        title: 'Invoice after each visit',
        description: 'Completed cleans generate invoices and payment links.',
      },
    ],
    faqs: [
      {
        q: 'What is maid scheduling software?',
        a: 'Maid scheduling software manages bookings, cleaner assignment and recurring visits for house cleaning businesses. ServiceApp.ai also quotes, invoices and collects payment in the same system.',
      },
      {
        q: 'Can maid scheduling software prevent double-booking?',
        a: 'Yes. ServiceApp.ai shows every job and cleaner on one calendar so overlapping visits are visible before you confirm a booking.',
      },
    ],
    relatedSlugs: ['/maid-service-software', '/cleaning-service-app', '/cleaning-service-software'],
  },
  ...EXTRA_INDUSTRY_SEO_PAGES,
];

export const INDUSTRY_SEO_SLUGS = INDUSTRY_SEO_PAGES.map((p) => p.slug);

export function getIndustrySeoPageByPath(pathname: string): IndustrySeoPage | undefined {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  return INDUSTRY_SEO_PAGES.find((p) => p.slug === normalized);
}

export function getIndustrySeoPageBySlugParam(slug: string): IndustrySeoPage | undefined {
  const path = slug.startsWith('/') ? slug : `/${slug}`;
  return getIndustrySeoPageByPath(path);
}
